'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import { Eye, EyeOff, Loader2, Copy, Check as CheckIcon } from 'lucide-react';


interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const demoCredentials = { email: 'jordan@austinpropainters.com', password: 'Leverage2026!' };

export default function LoginForm({ onSwitchToSignup }: { onSwitchToSignup: () => void }) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<'email' | 'password' | null>(null);

  const { register, handleSubmit, setValue, formState: { errors }, setError } = useForm<LoginFormData>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const handleCopy = async (field: 'email' | 'password') => {
    const val = field === 'email' ? demoCredentials.email : demoCredentials.password;
    await navigator.clipboard.writeText(val);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const autofillDemo = () => {
    setValue('email', demoCredentials.email);
    setValue('password', demoCredentials.password);
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    // TODO: POST /api/auth/login — validate against Supabase auth.users, return JWT session
    await new Promise((r) => setTimeout(r, 1200));

    if (data.email !== demoCredentials.email || data.password !== demoCredentials.password) {
      setIsLoading(false);
      setError('email', { message: 'Invalid credentials — use the demo accounts below to sign in' });
      return;
    }

    setIsLoading(false);
    toast.success('Welcome back, Jordan!');
    setTimeout(() => router.push('/decision-dashboard'), 600);
  };

  return (
    <>
      <Toaster position="bottom-right" theme="dark" />
      <div className="animate-slide-up">
        <h2 className="text-2xl font-800 text-white mb-1">Welcome back</h2>
        <p className="text-[#666] text-sm mb-8">Sign in to your Serious Quotes account</p>

        {/* Google SSO */}
        <button
          type="button"
          className="w-full flex items-center justify-center gap-3 leverage-btn-secondary py-3 mb-6"
          onClick={() => toast.info('Google OAuth — connect Supabase Auth provider')}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
            <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          <span className="text-sm font-600">Continue with Google</span>
        </button>

        <div className="flex items-center gap-3 mb-6">
          <hr className="flex-1 border-[#1F1F1F]" />
          <span className="text-[#444] text-xs font-500">or sign in with email</span>
          <hr className="flex-1 border-[#1F1F1F]" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="leverage-label">Email Address</label>
            <input
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
              })}
              type="email"
              placeholder="jordan@austinpropainters.com"
              className="leverage-input"
              autoComplete="email"
            />
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="leverage-label mb-0">Password</label>
              <button type="button" className="text-blue-400 text-xs hover:text-blue-300 transition-colors">
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <input
                {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Minimum 6 characters' } })}
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className="leverage-input pr-11"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#555] hover:text-white transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password.message}</p>}
          </div>

          <div className="flex items-center gap-2">
            <input
              {...register('rememberMe')}
              type="checkbox"
              id="remember-me"
              className="w-4 h-4 rounded border-[#3A3A3A] bg-[#0D0D0D] accent-blue-600"
            />
            <label htmlFor="remember-me" className="text-[#888] text-sm cursor-pointer">
              Remember me for 30 days
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="leverage-btn-primary w-full py-3.5 text-base flex items-center justify-center gap-2"
            style={{ minWidth: '100%' }}
          >
            {isLoading ? (
              <><Loader2 size={18} className="animate-spin" /> Signing in...</>
            ) : (
              'Sign In →'
            )}
          </button>
        </form>

        {/* Demo credentials box */}
        <div className="mt-6 p-4 bg-[#0D0D0D] border border-[#2A2A2A] rounded-xl">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[#666] text-xs font-600 uppercase tracking-wide">Demo Account</p>
            <button
              onClick={autofillDemo}
              className="text-blue-400 text-xs font-600 hover:text-blue-300 transition-colors px-2 py-1 bg-blue-600/10 rounded"
            >
              Autofill
            </button>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Email', value: demoCredentials.email, field: 'email' as const },
              { label: 'Password', value: demoCredentials.password, field: 'password' as const },
            ].map(({ label, value, field }) => (
              <div key={`cred-${field}`} className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <span className="text-[#444] text-[10px] uppercase tracking-wide">{label}</span>
                  <p className="text-[#AAA] text-xs font-mono truncate">{field === 'password' ? '••••••••••••' : value}</p>
                </div>
                <button
                  onClick={() => handleCopy(field)}
                  className="flex-shrink-0 w-7 h-7 rounded-lg bg-[#1A1A1A] hover:bg-[#2A2A2A] flex items-center justify-center text-[#555] hover:text-white transition-colors"
                  title={`Copy ${label}`}
                >
                  {copiedField === field ? <CheckIcon size={12} className="text-emerald-400" /> : <Copy size={12} />}
                </button>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-sm text-[#555] mt-6">
          Don&apos;t have an account?{' '}
          <button onClick={onSwitchToSignup} className="text-blue-400 hover:text-blue-300 font-600 transition-colors">
            Start free trial
          </button>
        </p>
      </div>
    </>
  );
}